import { DispenserTimelineItem } from '@/features/dispensers/services/schemas.ts';
import { Timeline } from '@/common/components/timeline/Timeline.tsx';
import { isEventGroup } from '@/features/events/components/events-timeline/utils/isEventGroup.ts';
import { EventTimelineGroupItem } from '@/features/events/components/events-timeline/subcomponents/event-timeline-group-item/EventTimelineGroupItem.tsx';
import { EventTimelineItem } from '@/features/events/components/events-timeline/subcomponents/event-timeline-item/EventTimelineItem.tsx';
import { EventTypeEnum, getEventTypeLabel } from '@/features/events/types/EventType.ts';
import { getEventTimelineItemProps } from '@/features/events/components/events-timeline/utils/getEventTimelineItemProps.tsx';
import { useTheme } from '@mui/material/styles';
import { ErrorLabel } from '@/features/events/components/events-timeline/subcomponents/error-label/ErrorLabel.tsx';
import { isEventResultError } from '@/features/events/types/EventResult.ts';
import { red } from '@mui/material/colors';
import { ReactNode } from 'react';
import { Trans } from '@lingui/react/macro';
import { ScrollTrigger } from '@/common/components/scroll-trigger/ScrollTrigger.tsx';

export interface EventsTimelineProps {
  items: DispenserTimelineItem[];
  onShowChildEvents?: (item: DispenserTimelineItem) => void;
  canLoadMore?: boolean;
  isLoading?: boolean;
  onLoadMore?: () => void;
  loadingError?: ReactNode;
  loadingErrorDescription?: ReactNode;
}

export const EventsTimeline = ({
  items,
  onShowChildEvents,
  canLoadMore,
  onLoadMore,
  loadingError,
  loadingErrorDescription,
  isLoading,
}: EventsTimelineProps) => {
  const theme = useTheme();

  const footer = (() => {
    if (loadingError) {
      return (
        <Timeline.Error
          title={loadingError}
          description={loadingErrorDescription}
          isLast
          onTryAgain={onLoadMore}
        />
      );
    }

    if (isLoading) {
      return <Timeline.Loading label={<Trans>Loading more events</Trans>} />;
    }

    if (canLoadMore) {
      return (
        <Timeline.LoadMore onClick={onLoadMore} buttonLabel={<Trans>Load more events</Trans>} />
      );
    }

    return null;
  })();

  return (
    <Timeline
      backgroundColor={theme.palette.background.paper}
      leftContentProps={{ width: 230, display: { xs: 'none', md: 'initial' } }}
    >
      {items.map((item, idx) => {
        const prevEvent = items.at(idx + 1);
        const eventItemProps = getEventTimelineItemProps(item);
        const isLast = idx + 1 === items.length && !footer;

        if (isEventGroup(item)) {
          return (
            <EventTimelineGroupItem
              key={idx}
              {...eventItemProps}
              isLast={isLast}
              timeStart={item.startTime}
              timeStartUtcOffset={item.startTimeUtcOffset}
              timeEnd={item.endTime}
              timeEndUtcOffset={item.endTimeUtcOffset}
              title={getEventTypeLabel(item.eventType)}
              childCount={item.eventsCount}
              errorCount={item.resultsErrorCount}
              prevEventAt={prevEvent?.startTime}
              onShowChildEvents={() => onShowChildEvents?.(item)}
              labels={
                <>
                  {eventItemProps.labels}
                  {item.resultsErrorCount > 0 && <ErrorLabel errorCount={item.resultsErrorCount} />}
                </>
              }
            />
          );
        }

        const hasError = isEventResultError(item.result) && item.eventType !== EventTypeEnum.ERROR;
        return (
          <EventTimelineItem
            key={item.id ?? idx}
            {...eventItemProps}
            isLast={isLast}
            icon={
              <Timeline.Icon
                {...eventItemProps.iconProps}
                color={hasError ? red : eventItemProps.iconProps.color}
              />
            }
            eventAt={item.startTime}
            eventUtcOffset={item.startTimeUtcOffset}
            title={getEventTypeLabel(item.eventType)}
            prevEventAt={prevEvent?.startTime}
            labels={
              <>
                {eventItemProps.labels}
                {hasError && <ErrorLabel />}
              </>
            }
          />
        );
      })}
      {footer}

      {canLoadMore && <ScrollTrigger offsetFromEnd={700} onIntersected={onLoadMore} />}
    </Timeline>
  );
};
